import CustomButton from "@components/buttons/CustomButton";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { primeServices } from "@services/apis/Prime";
import { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";

interface ConfirmRefundPanelProps {
  open: boolean;
  instanceId?: string;
  market: "canada" | "europe";
  onClose: () => void;
  onSuccess?: (instanceId: string, newEventId: number) => void;
}

const ConfirmRefundPanel = ({
  open,
  instanceId,
  market,
  onClose,
  onSuccess,
}: ConfirmRefundPanelProps) => {
  const { handleError, handleSuccess } = useSnackbarMessage();
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const canRefund = confirmation.trim() === "REFUND";

  useEffect(() => {
    setConfirmation("");
  }, [open, instanceId]);

  const handleRefund = async () => {
    if (!instanceId || !canRefund) return;
    setLoading(true);
    try {
      const response =
        market === "canada"
          ? await primeServices.cadPayinRefund(instanceId)
          : await primeServices.ivyHoldRefund(instanceId);
      handleSuccess({
        message: response?.msg || "Refund initialized successfully",
      });
      onSuccess?.(instanceId, market === "canada" ? 10 : 11);
      onClose();
    } catch (error: any) {
      handleError({
        message:
          error?.response?.data?.msg ||
          error?.message ||
          "Failed to refund instance",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <SlidingPanel
      isOpen={open && !!instanceId}
      onClose={loading ? () => {} : onClose}
      onClickCross={loading ? () => {} : onClose}
      heading="Confirm Refund"
      showConfirm={false}
      showCross={!loading}
      isMobile={isMobile}
    >
      <div className="p-4 flex flex-col gap-4">
        <p className="text-xs text-text-subtle">
          You are about to refund instance{" "}
          <strong className="text-text-base">{instanceId}</strong>. This action
          applies only to external transfers that meet the event-log rules. Do
          you want to continue?
        </p>
        <GlobalTextInputField
          label="Type REFUND to confirm"
          placeholder="REFUND"
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
          disabled={loading}
        />
        <div className="flex flex-col gap-2">
          <CustomButton
            text="REFUND"
            isActive={canRefund}
            isButtonLoading={loading}
            onClick={handleRefund}
            buttonColor="bg-bg-button-delete"
            borderColor="border-border-button-delete"
          />
          <CustomButton
            text="CANCEL"
            isActive={!loading}
            isHollow
            onClick={onClose}
          />
        </div>
      </div>
    </SlidingPanel>
  );
};

export default ConfirmRefundPanel;
